import { c as _c } from "react/compiler-runtime";
import { type InfiniteData, infiniteQueryOptions, keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import type { APISearchSelectRemoteResults } from "@/components/ui/select/api-search-select";
import { queryClient } from "@/config/react-query";
import { useDebounce } from "@/hooks/use-debounce";
import type { AccessLabPanel, AccessLabTestCode, JunctionPayor, LabBiomarkerHistory, LabOrderFetchParams, LabOrderListItem, LabOrderTableItem, LabTest, Marker } from "@/types/lab";
import type { Page } from "@/types/pagination";
import { fetchAccessCollectionInstructions, fetchAccessLabPanels, fetchAllLabOrders, fetchCollectionInstructionsPdf, fetchInHouseLabMenu, fetchLabAccounts, fetchLabBiomarkerHistory, fetchLabOrder, fetchLabOrderEmbedUrl, fetchLabOrders, fetchLabResultsReadyCount, fetchLabTests, fetchLabs, fetchLastPayor, fetchMarkers, searchAccessLabTests, searchMarkers, searchPayors } from "./lab";
import type { MarkersResponse } from "./lab";

// À-la-carte marker search fires once 2+ characters are typed (the compendium
// is too large to list unfiltered); shorter queries stay disabled.
const MARKER_SEARCH_MIN_CHARS = 2;
const PAYOR_SEARCH_MIN_CHARS = 2;
export const labQueryKeys = {
  all: ["lab"] as const,
  tests: (params: Record<string, unknown>) => [...labQueryKeys.all, "tests", params] as const,
  labs: () => [...labQueryKeys.all, "labs"] as const,
  labAccounts: () => [...labQueryKeys.all, "labAccounts"] as const,
  labTestMarkers: (labTestId: string) => [...labQueryKeys.all, "labTestMarkers", labTestId] as const,
  accessPanels: () => [...labQueryKeys.all, "accessPanels"] as const,
  inHouseMenu: () => [...labQueryKeys.all, "inHouseMenu"] as const,
  accessTestSearch: (params: {
    query: string;
    perPage: number;
  }) => [...labQueryKeys.all, "accessTestSearch", params] as const,
  markerSearch: (params: {
    name: string;
    labSlug?: string;
  }) => [...labQueryKeys.all, "markerSearch", params] as const,
  orders: (patientId: string) => [...labQueryKeys.all, "orders", patientId] as const,
  order: (orderId: string) => [...labQueryKeys.all, "order", orderId] as const,
  collectionInstructions: (orderId: string) => [...labQueryKeys.all, "collectionInstructions", orderId] as const,
  accessCollectionInstructions: (orderId: string) => [...labQueryKeys.all, "accessCollectionInstructions", orderId] as const,
  biomarkers: (patientId: string) => [...labQueryKeys.all, "biomarkers", patientId] as const,
  // Distinct from `orders(patientId)` above — the chart tab's unfiltered,
  // unpaginated per-patient list. This is the clinic-wide paginated list.
  orderList: (filters: LabOrderFetchParams) => [...labQueryKeys.all, "orderList", filters] as const,
  // The patient sidebar's newest-first pages. Infinite-query data, so it can't
  // share `orders` (an array) or `orderList` (a single Page).
  patientOrderPages: (patientId: string, perPage: number) => [...labQueryKeys.all, "patientOrderPages", patientId, perPage] as const,
  embedUrl: (orderId: string) => [...labQueryKeys.all, "embedUrl", orderId] as const,
  lastPayor: (params: {
    patientId: string;
    insuranceCoverageId: string;
  }) => [...labQueryKeys.all, "lastPayor", params] as const,
  payorSearch: (name: string) => [...labQueryKeys.all, "payorSearch", name] as const,
  // Kept outside labOrderListKeyPrefix: setQueriesData<Page<…>> patches everything under it.
  resultsReadyCount: () => [...labQueryKeys.all, "resultsReadyCount"] as const
};

// Matches every cached `orderList` filter/page combination — for prefix-scoped
// cache writes (the results-viewed optimistic patch, realtime invalidation)
// that must reach every page, not just whichever filter produced the query
// the user currently has open.
export const labOrderListKeyPrefix = [...labQueryKeys.all, "orderList"] as const;

// Every patient's sidebar pages, for the same prefix-scoped writes.
export const labPatientOrderPagesKeyPrefix = [...labQueryKeys.all, "patientOrderPages"] as const;
export const PATIENT_LAB_ORDER_PAGE_SIZE = 5;

// Same reasoning for the Junction catalog. Narrower than `labQueryKeys.all`, which would
// also re-mint the short-lived `embedUrl` and refetch every order list.
export const labTestsKeyPrefix = [...labQueryKeys.all, "tests"] as const;

// Hoisted: `select` runs on every observer update, and a fresh object literal
// each time makes the result a new reference whenever the query has no data.
const EMPTY_ORDER_PAGE: Page<LabOrderTableItem> = {
  data: [],
  total: 0
};
export const labQueryOptions = {
  tests: (params: {
    name?: string;
    labSlug?: string;
    collectionMethod?: string;
    cursor?: string;
  } = {}) => ({
    queryKey: labQueryKeys.tests(params),
    queryFn: () => fetchLabTests(params),
    select: (data: LabTest[] | null) => data ?? []
  }),
  // No select coercion, as for `tests`: null (couldn't load) stays distinct from [].
  accessPanels: () => ({
    queryKey: labQueryKeys.accessPanels(),
    queryFn: (): Promise<AccessLabPanel[] | null> => fetchAccessLabPanels()
  }),
  inHouseMenu: () => ({
    queryKey: labQueryKeys.inHouseMenu(),
    queryFn: () => fetchInHouseLabMenu()
  }),
  labs: () => ({
    queryKey: labQueryKeys.labs(),
    queryFn: () => fetchLabs()
  }),
  labAccounts: () => ({
    queryKey: labQueryKeys.labAccounts(),
    queryFn: () => fetchLabAccounts()
    // No select coercion here: keep undefined (loading) and null (fetch failed)
    // distinct from [] (loaded, zero accounts) so the cart can block placement
    // until accounts are actually known, rather than treating unknown as zero.
  }),
  labTestMarkers: (labTestId: string) => ({
    queryKey: labQueryKeys.labTestMarkers(labTestId),
    // Throw on failure (catchError=false) so a failed marker load enters the
    // query's error state and isn't recorded as an empty-but-ready panel — the
    // submit gate reads data-presence, and React Query will retry the fetch.
    queryFn: () => fetchMarkers(labTestId, false),
    select: (data: Marker[] | null) => data ?? [],
    enabled: !!labTestId
  }),
  // À-la-carte compendium search. `labSlug` scopes results to a single lab
  // (single-lab mode); omit it to search the full compendium (multi-lab).
  markerSearch: (params: {
    name: string;
    labSlug?: string;
  }) => ({
    queryKey: labQueryKeys.markerSearch(params),
    queryFn: () => searchMarkers({
      name: params.name,
      labSlug: params.labSlug,
      aLaCarteEnabled: true,
      size: 50
    }),
    select: (data: MarkersResponse | null) => data?.markers ?? [],
    enabled: params.name.trim().length >= MARKER_SEARCH_MIN_CHARS
  }),
  orders: (patientId: string) => ({
    queryKey: labQueryKeys.orders(patientId),
    queryFn: () => fetchLabOrders(patientId),
    select: (data: LabOrderListItem[] | null) => data ?? []
  }),
  biomarkers: (patientId: string) => ({
    queryKey: labQueryKeys.biomarkers(patientId),
    queryFn: () => fetchLabBiomarkerHistory(patientId),
    select: (data: LabBiomarkerHistory[] | null) => data ?? []
  }),
  patientOrderPages: (patientId: string) => infiniteQueryOptions({
    queryKey: labQueryKeys.patientOrderPages(patientId, PATIENT_LAB_ORDER_PAGE_SIZE),
    // Throws on failure so a failed read isn't shown as "No lab orders".
    queryFn: ({
      pageParam
    }) => fetchAllLabOrders({
      patientId,
      page: pageParam,
      perPage: PATIENT_LAB_ORDER_PAGE_SIZE,
      sortBy: "created_date",
      sortDirection: "desc"
    }, false),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages, lastPageParam) => {
      const loaded = pages.reduce((n, page) => n + (page?.data.length ?? 0), 0);
      return lastPage && loaded < lastPage.total ? lastPageParam + 1 : undefined;
    },
    enabled: !!patientId
  }),
  orderList: (filters: LabOrderFetchParams) => ({
    queryKey: labQueryKeys.orderList(filters),
    queryFn: () => fetchAllLabOrders(filters),
    select: (data: Page<LabOrderTableItem> | null) => data ?? EMPTY_ORDER_PAGE
  }),
  order: (orderId: string) => ({
    queryKey: labQueryKeys.order(orderId),
    queryFn: () => fetchLabOrder(orderId),
    enabled: !!orderId
  }),
  // Fetched when the order opens so the button is only clickable once Junction
  // has built the PDF. A ready PDF never changes, so it is kept; a not-ready
  // answer is kept for a minute, then checked again the next time the order
  // is opened or refocused.
  collectionInstructions: (orderId: string) => ({
    queryKey: labQueryKeys.collectionInstructions(orderId),
    queryFn: () => fetchCollectionInstructionsPdf(orderId),
    enabled: !!orderId,
    staleTime: (query: {
      state: {
        data: Blob | null | undefined;
      };
    }) => query.state.data ? Infinity : 60_000,
    refetchOnWindowFocus: true,
    // A not-ready 404 resolves to null, so this only retries real failures.
    retry: 2
  }),
  accessCollectionInstructions: (orderId: string) => ({
    queryKey: labQueryKeys.accessCollectionInstructions(orderId),
    queryFn: () => fetchAccessCollectionInstructions(orderId)
  }),
  // Junction App Embed URL. Short-lived (Junction returns an expiring token),
  // so keep staleTime/gcTime tight — a re-open re-mints rather than reuse a
  // stale URL. refetchOnWindowFocus is off so regaining focus never reloads the
  // live iframe. Active-tab gating is the conditional mount in lab-order-detail
  // (the component renders only while the Junction tab is open), so the query
  // fires on tab open.
  embedUrl: (orderId: string) => ({
    queryKey: labQueryKeys.embedUrl(orderId),
    queryFn: () => fetchLabOrderEmbedUrl(orderId),
    enabled: !!orderId,
    staleTime: 1000 * 30,
    gcTime: 1000 * 60,
    refetchOnWindowFocus: false
  }),
  lastPayor: (params: {
    patientId: string;
    insuranceCoverageId: string;
  }) => ({
    queryKey: labQueryKeys.lastPayor(params),
    queryFn: () => fetchLastPayor(params),
    enabled: !!params.patientId && !!params.insuranceCoverageId
  }),
  payorSearch: (name: string) => ({
    queryKey: labQueryKeys.payorSearch(name),
    queryFn: () => searchPayors(name),
    select: (data: JunctionPayor[] | null) => data ?? [],
    enabled: name.trim().length >= PAYOR_SEARCH_MIN_CHARS
  }),
  resultsReadyCount: () => ({
    queryKey: labQueryKeys.resultsReadyCount(),
    queryFn: () => fetchLabResultsReadyCount(),
    staleTime: 30 * 1000,
    select: (data: number | null) => data ?? 0
  })
};

/**
 * Move the Labs sidebar badge by `delta` without refetching it — mirrors
 * adjustSocialUnreadCount. A cache that was never populated is left alone.
 */
export function adjustLabResultsReadyCountInCache(delta: number): void {
  queryClient.setQueryData<number | null>(labQueryKeys.resultsReadyCount(), prev => typeof prev === "number" ? Math.max(0, prev + delta) : prev);
}

/** Set the badge to the server's absolute count, even into an empty cache. */
export function setLabResultsReadyCountInCache(count: number): void {
  queryClient.setQueryData<number | null>(labQueryKeys.resultsReadyCount(), count);
}
type LabOrderPage = Page<LabOrderTableItem> | null;

/** This order's cached viewed-at, from whichever per-patient list holds it. */
export function getLabOrderViewedAtFromCache(patientId: string, orderId: string): string | undefined {
  const fromList = queryClient.getQueryData<LabOrderListItem[] | null>(labQueryKeys.orders(patientId))?.find(o => o.id === orderId);
  if (fromList) return fromList.resultsViewedAt;
  return queryClient.getQueryData<InfiniteData<LabOrderPage>>(labQueryKeys.patientOrderPages(patientId, PATIENT_LAB_ORDER_PAGE_SIZE))?.pages.flatMap(page => page?.data ?? []).find(o => o.id === orderId)?.resultsViewedAt;
}

/**
 * Write an order's viewed-at into every cached list that shows it: the patient
 * Labs tab, the sidebar pages and the clinic-wide list. Only that field is
 * touched, so concurrent realtime patches to the same caches survive.
 */
export function setLabOrderViewedAtInCaches(patientId: string, orderId: string, resultsViewedAt: string | undefined): void {
  const patch = <T extends {
    id: string;
  },>(o: T): T => o.id === orderId ? {
    ...o,
    resultsViewedAt
  } : o;
  const patchPage = <P extends LabOrderPage | undefined,>(page: P): P => page ? {
    ...page,
    data: page.data.map(patch)
  } : page;
  queryClient.setQueryData<LabOrderListItem[] | null>(labQueryKeys.orders(patientId), prev => prev?.map(patch) ?? prev);
  queryClient.setQueriesData<InfiniteData<LabOrderPage>>({
    queryKey: [...labPatientOrderPagesKeyPrefix, patientId]
  }, prev => prev ? {
    ...prev,
    pages: prev.pages.map(patchPage)
  } : prev);
  queryClient.setQueriesData<LabOrderPage>({
    queryKey: labOrderListKeyPrefix
  }, patchPage);
}
const ACCESS_TEST_SEARCH_PER_PAGE = 20;
const EMPTY_ACCESS_TEST_PAGE: Page<AccessLabTestCode> = {
  data: [],
  total: 0
};

/**
 * Paged Access compendium search for the order drawer's test picker, in the
 * shape `APISearchSelect`'s `remote` prop takes. Same as `useIcd10Search`,
 * except an empty term browses the whole compendium instead of showing nothing.
 */
export function useAccessLabTestSearch(t0) {
  const $ = _c(22);
  const enabled = t0 === undefined ? true : t0;
  const [term, setTerm] = useState("");
  let t1;
  if ($[0] !== term) {
    t1 = term.trim();
    $[0] = term;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const typed = t1;
  const query = useDebounce(typed, 300);
  let t2;
  if ($[2] !== query) {
    t2 = labQueryKeys.accessTestSearch({
      query,
      perPage: ACCESS_TEST_SEARCH_PER_PAGE
    });
    $[2] = query;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== query) {
    t3 = async t4 => {
      const {
        pageParam
      } = t4;
      return (await searchAccessLabTests({
        query,
        page: pageParam,
        perPage: ACCESS_TEST_SEARCH_PER_PAGE
      })) ?? EMPTY_ACCESS_TEST_PAGE;
    };
    $[4] = query;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== enabled || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      getNextPageParam: _temp2,
      initialPageParam: 1,
      placeholderData: keepPreviousData,
      enabled
    };
    $[6] = enabled;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    data,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = useInfiniteQuery(t4);
  data?.pages;
  let t5;
  if ($[10] !== data?.pages) {
    t5 = data?.pages.flatMap(_temp3) ?? [];
    $[10] = data?.pages;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const results = t5;
  let t6;
  if ($[12] !== fetchNextPage || $[13] !== hasNextPage || $[14] !== isFetchingNextPage) {
    t6 = () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage({
          cancelRefetch: false
        });
      }
    };
    $[12] = fetchNextPage;
    $[13] = hasNextPage;
    $[14] = isFetchingNextPage;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const onLoadMore = t6;
  const isLoading = typed !== query || isFetching && !isFetchingNextPage;
  const t7 = !!hasNextPage;
  let t8;
  if ($[16] !== isFetchingNextPage || $[17] !== isLoading || $[18] !== onLoadMore || $[19] !== results || $[20] !== t7) {
    t8 = {
      results,
      onSearchTermChange: setTerm,
      isLoading,
      hasNextPage: t7,
      isFetchingNextPage,
      onLoadMore
    };
    $[16] = isFetchingNextPage;
    $[17] = isLoading;
    $[18] = onLoadMore;
    $[19] = results;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
}
function _temp3(page_0) {
  return page_0.data;
}
function _temp2(lastPage, allPages) {
  const loadedCount = allPages.reduce(_temp, 0);
  return loadedCount < lastPage.total ? allPages.length + 1 : undefined;
}
function _temp(sum, page) {
  return sum + page.data.length;
}